<template>
  <div class="expertise-step">
    <h2 class="step-title">Demographics & Geographic Expertise</h2>
    <p class="step-description">Tell us about your expertise with different demographics and regions</p>
    
    <!-- Loading state -->
    <div v-if="loadingFormOptions" class="loading-container">
      <v-progress-circular indeterminate color="primary" size="40" />
      <p>Loading geographic options...</p>
    </div>
    
    <div v-else class="expertise-content">
      <!-- Age Demographics Expertise -->
      <div class="form-group">
        <label class="expertise-label">Age Demographics Expertise</label>
        <p class="expertise-sublabel">Rate your experience working with the following age groups</p>
        
        <div class="rating-grid">
          <div class="rating-card" :class="{ active: ageExpertise.children > 0 }">
            <div class="rating-icon">
              <v-icon size="large">mdi-account-school</v-icon>
            </div>
            <div class="rating-title">Young Adults</div>
            <div class="rating-description">18-25 years</div>
            <div class="rating-options">
              <div 
                v-for="level in 3" 
                :key="level"
                class="rating-dot"
                :class="{ active: ageExpertise.children >= level }"
                @click="rateAgeExpertise('children', level)"
              ></div>
            </div>
          </div>
          
          <div class="rating-card" :class="{ active: ageExpertise.teenagers > 0 }">
            <div class="rating-icon">
              <v-icon size="large">mdi-briefcase</v-icon>
            </div>
            <div class="rating-title">Adults</div>
            <div class="rating-description">25-40 years</div>
            <div class="rating-options">
              <div 
                v-for="level in 3" 
                :key="level"
                class="rating-dot"
                :class="{ active: ageExpertise.teenagers >= level }"
                @click="rateAgeExpertise('teenagers', level)"
              ></div>
            </div>
          </div>
          
          <div class="rating-card" :class="{ active: ageExpertise.adults > 0 }">
            <div class="rating-icon">
              <v-icon size="large">mdi-account-supervisor</v-icon>
            </div>
            <div class="rating-title">Middle-Aged Adults</div>
            <div class="rating-description">40-50 years</div>
            <div class="rating-options">
              <div 
                v-for="level in 3" 
                :key="level"
                class="rating-dot"
                :class="{ active: ageExpertise.adults >= level }"
                @click="rateAgeExpertise('adults', level)"
              ></div>
            </div>
          </div>
          
          <div class="rating-card" :class="{ active: ageExpertise.seniors > 0 }">
            <div class="rating-icon">
              <v-icon size="large">mdi-account-supervisor-circle</v-icon>
            </div>
            <div class="rating-title">Senior Adults</div>
            <div class="rating-description">50+ years</div>
            <div class="rating-options">
              <div 
                v-for="level in 3" 
                :key="level"
                class="rating-dot"
                :class="{ active: ageExpertise.seniors >= level }"
                @click="rateAgeExpertise('seniors', level)"
              ></div>
            </div>
          </div>
        </div>
        <div class="rating-legend">
          <div class="legend-item">
            <div class="legend-dot"></div>
            <div class="legend-text">Low Experience</div>
          </div>
          <div class="legend-item">
            <div class="legend-dot active"></div>
            <div class="legend-text">High Experience</div>
          </div>
        </div>
        <div v-if="ageError" class="error-message">{{ ageError }}</div>
      </div>
      
      <!-- Geographic Expertise -->
      <MultiSelectWithOther
        label="Geographic Expertise"
        description="Select all regions where you have experience working"
        :options="regions"
        v-model="selectedRegions"
        :allow-other="false"
        option-key="id"
        option-label="name"
        :error="geoError"
      />
      
      <div class="action-buttons">
        <button 
          class="btn btn-back"
          @click="handleBack"
          :disabled="isSubmitting"
        >
          <v-icon>mdi-arrow-left</v-icon> Back
        </button>
        
        <button 
          class="btn btn-continue"
          @click="handleContinue"
          :disabled="isSubmitting"
        >
          <template v-if="isSubmitting">
            <v-progress-circular indeterminate color="white" size="20" class="spinner" />
          </template>
          <template v-else>
            Continue <v-icon>mdi-arrow-right</v-icon>
          </template>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import MultiSelectWithOther from '@/components/common/MultiSelectWithOther.vue';
import apiService from '@/services/apiService';

export default {
  name: 'RegisterStep7',
  components: {
    MultiSelectWithOther
  },
  props: {
    formData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      ageExpertise: {
        children: this.formData.ageExpertise?.children || 0,
        teenagers: this.formData.ageExpertise?.teenagers || 0,
        adults: this.formData.ageExpertise?.adults || 0,
        seniors: this.formData.ageExpertise?.seniors || 0
      },
      formOptions: this.formData.formOptions || {},
      loadingFormOptions: false,
      selectedRegions: this.formData.geographicalExpertise || [],
      isSubmitting: false,
      error: '',
      generalError: '',
      ageError: '',
      geoError: ''
    };
  },
  computed: {
    regions() {
      return this.formOptions.geographicRegions || [];
    }
  },
  async mounted() {
    // If form options weren't passed from previous step, fetch them
    if (!this.formOptions.geographicRegions) {
      await this.fetchFormOptions();
    }
  },
  methods: {
    async fetchFormOptions() {
      this.loadingFormOptions = true;
      try {
        const response = await apiService.get('experts/form-options/');
        this.formOptions = response.data;
        console.log('Form options loaded in step 7:', this.formOptions);
      } catch (error) {
        console.error('Failed to load form options:', error);
        this.generalError = 'Failed to load form options. Please refresh the page and try again.';
      } finally {
        this.loadingFormOptions = false;
      }
    },
    rateAgeExpertise(group, level) {
      // If already selected, clicking again deselects
      if (this.ageExpertise[group] === level) {
        this.ageExpertise[group] = 0;
      } else {
        this.ageExpertise[group] = level;
      }
    },
    validateForm() {
      // Check if at least one age group and one region has been rated/selected
      const hasAgeExpertise = Object.values(this.ageExpertise).some(value => value > 0);
      const hasGeoExpertise = this.selectedRegions.length > 0;
      
      this.error = '';
      this.generalError = '';
      this.ageError = '';
      this.geoError = '';
      
      let isValid = true;
      
      if (!hasAgeExpertise) {
        this.ageError = 'Please rate at least one age group';
        isValid = false;
      }
      
      if (!hasGeoExpertise) {
        this.geoError = 'Please select at least one region';
        isValid = false;
      }
      
      return isValid;
    },
    handleBack() {
      this.$emit('prev-step');
    },
    handleContinue() {
      if (this.validateForm()) {
        this.isSubmitting = true;
        
        // Get the actual region objects for the selected IDs
        const selectedRegionObjects = this.regions.filter(region => 
          this.selectedRegions.includes(region.id)
        );
        
        const updatedData = {
          ...this.formData,
          ageExpertise: this.ageExpertise,
          geographicalExpertise: this.selectedRegions,
          geographicalExpertiseDetails: selectedRegionObjects,
          // Pass form options to next step
          formOptions: this.formOptions
        };
        
        // Emit updated form data
        this.$emit('update:form-data', updatedData);
        
        // Move to next step
        this.$emit('next-step');
        
        this.isSubmitting = false;
      }
    }
  }
};
</script>

<style scoped>
.expertise-step {
  background: white;
  border-radius: 8px;
  padding: 30px;
  max-width: 900px;
  margin: 0 auto;
}

.step-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  text-align: center;
}

.step-description {
  text-align: center;
  color: #666;
  margin-bottom: 30px;
}

.loading-container {
  text-align: center;
  padding: 40px;
  color: #666;
}

.loading-container p {
  margin-top: 15px;
}

.expertise-content {
  padding: 0 10px;
}

.form-group {
  margin-bottom: 40px;
}

.expertise-label {
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 15px;
}

.expertise-sublabel {
  font-size: 14px;
  color: #666;
  margin-bottom: 15px;
}

/* Age Demographics Rating */
.rating-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 15px;
}

.rating-card {
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: all 0.2s ease;
}

.rating-card.active {
  border-color: #e94057;
  background-color: rgba(233, 64, 87, 0.05);
}

.rating-icon {
  margin-bottom: 15px;
  color: #333;
}

.rating-card.active .rating-icon {
  color: #e94057;
}

.rating-title {
  font-weight: 600;
  font-size: 18px;
  margin-bottom: 5px;
}

.rating-description {
  font-size: 14px;
  color: #666;
  margin-bottom: 15px;
}

.rating-options {
  display: flex;
  gap: 10px;
}

.rating-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid #ddd;
  cursor: pointer;
  transition: all 0.2s ease;
}

.rating-dot:hover {
  border-color: #e94057;
}

.rating-dot.active {
  background-color: #e94057;
  border-color: #e94057;
}

.rating-legend {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-top: 15px;
}

.legend-item {
  display: flex;
  align-items: center;
}

.legend-dot {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 2px solid #ddd;
  margin-right: 10px;
}

.legend-dot.active {
  background-color: #e94057;
  border-color: #e94057;
}

.legend-text {
  font-size: 14px;
  color: #666;
}

.action-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
}

.btn {
  padding: 15px 30px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
}

.btn-back {
  background-color: #f0f0f0;
  color: #333;
}

.btn-back:hover {
  background-color: #e0e0e0;
}

.btn-continue {
  background-color: #e94057;
  color: white;
}

.btn-continue:hover {
  background-color: #d03a4e;
}

.btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.error-message {
  color: #e94057;
  font-size: 14px;
  margin-top: 5px;
}

.general-error {
  text-align: center;
  margin-top: 20px;
}

@media (max-width: 768px) {
  .rating-grid {
    grid-template-columns: 1fr;
  }
}

.spinner { margin:0; }
</style> 